// Tour/Onboarding Modal Component
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Bot,
  GraduationCap,
  Zap,
  Calendar,
  Users,
  Sparkles,
} from 'lucide-react';

const TOUR_STEPS = [
  {
    title: "Welkom bij Adelaar AI! 🦅",
    description: "Ik ben je digitale collega bij Zorg-Vleugel. Ik help je met alles: van medicatievragen tot protocollen en huisafspraken.",
    icon: Bot,
    tip: "Je kunt me altijd een vraag stellen in je eigen woorden!"
  },
  {
    title: "Je Eerste Dienst",
    description: "Klik op 'Eerste Dienst' voor een complete gids over hoe je dag eruitziet, van ochtenddienst tot avonddienst.",
    icon: GraduationCap,
    tip: "Perfect voor ZZP'ers en nieuwe medewerkers!"
  },
  {
    title: "Snel Antwoord Nodig? ⚡",
    description: "Gebruik de snelkoppelingen om direct veelgestelde vragen te stellen. Of typ gewoon je vraag!",
    icon: Zap,
    tip: "Probeer: 'Hoe laat is het avondeten?' of 'Wie is de voorzitter?'"
  },
  {
    title: "Dagritme & Schema's",
    description: "Klik op 'Dagritme' bovenin voor alle schema's: maaltijden, dagbesteding, corvee, afvalkalender.",
    icon: Calendar,
    tip: "Alle belangrijke tijden en taken staan hier overzichtelijk!"
  },
  {
    title: "Cliënt Informatie",
    description: "Klik op een cliëntkaart voor het complete profiel: contacten, medicatie, zorgdoelen en signaleringsplan.",
    icon: Users,
    tip: "Belangrijk: Lees het signaleringsplan voor je eerste dienst!"
  },
  {
    title: "Klaar om te beginnen! 🦅",
    description: "Je bent helemaal klaar! Onthoud: ik ben er altijd om je te helpen. Vraag maar raak!",
    icon: Sparkles,
    tip: "Druk op 'Rondleiding' in het menu als je dit nog eens wilt zien."
  }
];

export const TourModal = ({ show, step, onNext, onPrev, onClose, onComplete }) => {
  const currentStep = TOUR_STEPS[step];
  const Icon = currentStep?.icon || Sparkles;

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="tour-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Progress */}
        <div className="h-1 bg-slate-100">
          <div 
            className="h-full bg-gradient-to-r from-[#1e3a5f] to-[#d4a853] transition-all duration-300"
            style={{ width: `${((step + 1) / TOUR_STEPS.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
              Stap {step + 1} van {TOUR_STEPS.length}
            </span>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="tour-close-btn">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{currentStep.title}</h3>
            <p className="text-gray-600">{currentStep.description}</p>
          </div>

          {/* Tip */}
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 mb-6">
            <div className="flex gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-800">{currentStep.tip}</p>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex gap-3">
            {step > 0 && (
              <Button variant="outline" onClick={onPrev} className="flex-1" data-testid="tour-prev-btn">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Vorige
              </Button>
            )}
            {step < TOUR_STEPS.length - 1 ? (
              <Button onClick={onNext} className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90" data-testid="tour-next-btn">
                Volgende
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={onComplete} className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90" data-testid="tour-complete-btn">
                Start!
                <Sparkles className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export { TOUR_STEPS };
